import type { MolecularWorkspace } from '@alchemiesolns/molecular-workspace';
import {
    MolecularGraph,
    molecularFeatureExtractor,
    featureSorter,
    featureIdentifier,
    type Feature,
    type GraphAtom,
} from '@alchemiesolns/feature-extractor';
import { toMolecularGraphJSON } from './toMolecularGraphJSON';

export type WorkspaceFeature = {
    feature: Feature;
    /** Computed display name (uniqueName || name) */
    displayName: string;
    /** Workspace atom IDs for this feature's atoms */
    atomIds: string[];
    /** Workspace atom IDs for this feature's connection atoms */
    connectionIds: string[];
};

export type WorkspaceFeatureResult = {
    features: WorkspaceFeature[];
    graph: MolecularGraph;
    indexToAtomId: string[];
};

/**
 * Extract chemical features from a MolecularWorkspace.
 *
 * Converts the workspace to MolecularGraphJSON, runs the full feature-extraction
 * pipeline (extract → sort → identify), and maps feature atoms back to workspace IDs.
 */
export function extractWorkspaceFeatures(workspace: MolecularWorkspace): WorkspaceFeatureResult {
    // 1. Convert workspace → graph JSON + ID mapping
    const { json, indexToAtomId } = toMolecularGraphJSON(workspace);

    // 2. Build the molecular graph
    const graph = new MolecularGraph(json);

    // 3. Stamp workspace atom IDs onto graph atoms (for atoms that came from the workspace)
    for (let i = 0; i < indexToAtomId.length; i++) {
        graph.atoms[i].id = indexToAtomId[i];
    }

    // 4. Extract → sort → identify
    const rawFeatures = molecularFeatureExtractor.extract(graph);
    const sortedFeatures = featureSorter.sort(rawFeatures);
    featureIdentifier.applyIdentifiers(sortedFeatures, graph);

    // 5. Map features to workspace IDs
    const features: WorkspaceFeature[] = sortedFeatures.map(feature => ({
        feature,
        displayName: feature.uniqueName || feature.name,
        atomIds: resolveAtomIds(feature.atoms, indexToAtomId),
        connectionIds: resolveAtomIds(feature.connections ?? [], indexToAtomId),
    }));

    return { features, graph, indexToAtomId };
}

function resolveAtomIds(atoms: GraphAtom[], indexToAtomId: string[]): string[] {
    const ids: string[] = [];
    for (const atom of atoms) {
        if (atom.index < indexToAtomId.length) {
            ids.push(indexToAtomId[atom.index]);
        }
        // Atoms beyond indexToAtomId are synthetic (implicit-H expanded by MolecularGraph) — skip
    }
    return ids;
}
